import { Component, ErrorInfo, ReactNode } from "react";
import { Box, Typography, Button } from "@mui/material";
import posthog from "posthog-js";
import { posthogApiKey } from "@/config/posthog";

interface Props {
  // Bumping resetKey unmounts the fallback and re-renders children.
  // Used when the user switches chats or clicks "Retry" so a render
  // failure in one chat doesn't poison the rest of the session.
  resetKey?: string | null;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  message?: string;
}

/**
 * Local error boundary for the chat-advisor messages area. The app
 * already has a global ErrorBoundary, but it swaps the entire page for
 * a "Something went wrong" splash — too disruptive for a render glitch
 * inside the chat thread (e.g. an unexpected message shape). This
 * boundary keeps the rest of the UI (sidebar, header, input bar)
 * mounted and shows an inline retry banner instead, so the user can
 * recover without a full reload.
 */
export default class ChatErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(err: Error): State {
    return { hasError: true, message: err?.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[ChatErrorBoundary] caught", error, info);
    if (posthogApiKey) {
      try {
        posthog.capture("$exception", {
          $exception_message: error.message,
          $exception_type: error.name,
          $exception_source: "ChatErrorBoundary",
          $exception_stack_trace_raw: error.stack,
          componentStack: info.componentStack,
        });
      } catch {
        // ignore
      }
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.state.hasError &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.setState({ hasError: false, message: undefined });
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false, message: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          flex={1}
          minHeight={240}
          gap={1.5}
          p={4}
          textAlign="center"
        >
          <Typography variant="h6">This chat hit a render error</Typography>
          <Typography variant="body2" color="text.secondary">
            {this.state.message ||
              "Something went wrong displaying this conversation."}
          </Typography>
          <Button variant="outlined" size="small" onClick={this.handleRetry}>
            Retry
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}
